/**
 * Shared node type constants — colors, icons, and labels for all pipeline step types.
 *
 * Canonical source of truth for visual representation of node types across
 * NodeInspectorPanel, DAGNode, and ChatPanel.
 */
import React from 'react';
import {
  Storage as SourceIcon,
  Transform as SqlIcon,
  Code as PythonIcon,
  CompareArrows as MatchIcon,
  Psychology as LlmIcon,
  Api as ApiIcon,
  Verified as GateIcon,
  CheckCircle as PassIcon,
  Output as OutputIcon,
  CloudUpload as StorageIcon,
  TableChart as PreviewIcon,
  AccountTree as AssemblyIcon,
} from '@mui/icons-material';

// ── Colors ────────────────────────────────────────────────────────────

export const NODE_COLORS: Record<string, string> = {
  source: '#64748b',
  source_group: '#4f46e5',
  sql_transform: '#0891b2',
  python_transform: '#7c3aed',
  fuzzy_match: '#d97706',
  llm_extract: '#ec4899',
  llm_classify: '#8b5cf6',
  api_enrich: '#059669',
  validation_gate: '#ea580c',
  mapping_output: '#059669',
  schema_map: '#059669',
  pipeline_output: '#2563eb',
  destination: '#7c3aed',
  target_table: '#0891b2',
  hierarchical_assembly: '#a855f7',
};

export function getNodeColor(stepType: string): string {
  return NODE_COLORS[stepType] || '#64748b';
}

// ── Icons ─────────────────────────────────────────────────────────────

/**
 * Icon component mapping. Each value is a SvgIcon component (not an element).
 * Consumers apply their own sx={{ fontSize, color }} to control sizing.
 */
const NODE_ICON_COMPONENTS: Record<string, React.ComponentType<any>> = {
  source: SourceIcon,
  source_group: SourceIcon,
  sql_transform: SqlIcon,
  python_transform: PythonIcon,
  fuzzy_match: MatchIcon,
  llm_extract: LlmIcon,
  llm_classify: LlmIcon,
  api_enrich: ApiIcon,
  validation_gate: GateIcon,
  mapping_output: PassIcon,
  schema_map: PassIcon,
  pipeline_output: OutputIcon,
  destination: StorageIcon,
  target_table: PreviewIcon,
  hierarchical_assembly: AssemblyIcon,
};

/**
 * Returns a rendered icon element for a node type at the specified size.
 * Falls back to the source icon if the type is unknown.
 */
export function getNodeIcon(stepType: string, fontSize: number = 16): React.ReactElement {
  const IconComponent = NODE_ICON_COMPONENTS[stepType] || SourceIcon;
  return React.createElement(IconComponent, { sx: { fontSize, color: '#fff' } });
}

// ── Labels ────────────────────────────────────────────────────────────

export const TYPE_LABELS: Record<string, string> = {
  sql_transform: 'SQL',
  python_transform: 'Python',
  fuzzy_match: 'Fuzzy Match',
  llm_extract: 'LLM Extract',
  llm_classify: 'LLM Classify',
  api_enrich: 'API Enrich',
  validation_gate: 'Validation',
  source: 'Source',
  source_group: 'Source Data',
  mapping_output: 'Schema Mapping',
  schema_map: 'Schema Map',
  pipeline_output: 'Pipeline Output',
  destination: 'Destination',
  target_table: 'Target Table',
  hierarchical_assembly: 'Assembly',
};

export function getTypeLabel(stepType: string): string {
  return TYPE_LABELS[stepType] || stepType.replace(/_/g, ' ');
}
